import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import {
	INSPIRATION_CURRENCY,
	OWN_HOME_RENT_FALLBACK,
} from "web_myplann/Inspiration/constants";

export const OWN_HOME_HORIZON_YEARS = 30;
const MORTGAGE_MONTHS = OWN_HOME_HORIZON_YEARS * 12;

const RATE_LOW = 0.06;
const RATE_MID = 0.05;
const RATE_HIGH = 0.04;

const PROPERTY_GROWTH_LOW = 1.02;
const PROPERTY_GROWTH_HIGH = 1.03;

const DSTI_RATIO = 0.55;
const CASHFLOW_RATIO = 0.8;
const DTI_INCOME_MULTIPLE = 9;
const MIN_DOWN_PAYMENT_RATIO = 0.1;
const MORTGAGE_LTV = 0.8;

export type OwnHomeState = "cold" | "affordability" | "full";

export type OwnHomeInputs = {
	rent?: number;
	income?: number;
	savings?: number;
	expenses?: number;
};

export type OwnHomeContext = {
	state: OwnHomeState;
	rent: number;
	income?: number;
	savings: number;
	expenses?: number;
	cashflow?: number;
	totalRent: number;
	priceLow: number;
	priceHigh: number;
	propertyLow: number;
	propertyHigh: number;
	maxPayment?: number;
	maxMortgage?: number;
	downPayment: number;
	savingsGap?: number;
};

const formatMillions = (value: number) => {
	const millions = value / 1_000_000;
	const rounded = millions >= 10 ? millions.toFixed(0) : millions.toFixed(1);
	return rounded.replace(".", ",");
};

export const formatMillionRange = (low: number, high: number) =>
	`${formatMillions(low)}–${formatMillions(high)} M ${getCurrencySymbol(INSPIRATION_CURRENCY)}`;

const roundToHundredThousand = (value: number) =>
	Math.round(value / 100_000) * 100_000;

export type OwnHomeGoalPreset = {
	totalValue: number;
	loanAmount: number;
	ownFunds: number;
	savingsTarget: number;
};

export const computeGoalPreset = (
	context: OwnHomeContext,
): OwnHomeGoalPreset => {
	const totalValue = roundToHundredThousand(context.priceHigh);
	const loanAmount = roundToHundredThousand(totalValue * MORTGAGE_LTV);
	const ownFunds = context.savings;
	return {
		totalValue,
		loanAmount,
		ownFunds,
		savingsTarget: Math.max(0, totalValue - loanAmount - ownFunds),
	};
};

const mortgageFromPayment = (payment: number, rate: number, months: number) => {
	const monthlyRate = rate / 12;
	return Math.round(
		payment * ((1 - (1 + monthlyRate) ** -months) / monthlyRate),
	);
};

const estimateAffordability = (income: number, expenses?: number) => {
	const dstiPayment = Math.round(income * DSTI_RATIO);
	const cashflowPayment =
		expenses !== undefined
			? Math.max(0, Math.round((income - expenses) * CASHFLOW_RATIO))
			: dstiPayment;
	const maxPayment = Math.min(dstiPayment, cashflowPayment);

	const fromPayment = mortgageFromPayment(
		maxPayment,
		RATE_MID,
		MORTGAGE_MONTHS,
	);
	const fromIncomeDti = Math.round(income * 12 * DTI_INCOME_MULTIPLE);
	const maxMortgage = Math.min(fromPayment, fromIncomeDti);

	return { maxPayment, maxMortgage };
};

const deriveState = (inputs: OwnHomeInputs): OwnHomeState => {
	if (inputs.rent && inputs.income && inputs.expenses !== undefined)
		return "full";
	if (inputs.rent && inputs.income) return "affordability";
	return "cold";
};

export const computeOwnHomeContext = (
	inputs: OwnHomeInputs,
): OwnHomeContext => {
	const state = deriveState(inputs);
	const rent = inputs.rent ?? 0;
	const savings = inputs.savings ?? 0;

	const mortgageLow =
		rent > 0 ? mortgageFromPayment(rent, RATE_LOW, MORTGAGE_MONTHS) : 0;
	const mortgageHigh =
		rent > 0 ? mortgageFromPayment(rent, RATE_HIGH, MORTGAGE_MONTHS) : 0;
	const mortgageMid =
		rent > 0 ? mortgageFromPayment(rent, RATE_MID, MORTGAGE_MONTHS) : 0;

	const priceLow = mortgageLow + savings;
	const priceHigh = mortgageHigh + savings;
	const priceMid = mortgageMid + savings;

	const affordability =
		inputs.income !== undefined
			? estimateAffordability(inputs.income, inputs.expenses)
			: undefined;

	const downPayment = affordability
		? Math.round(affordability.maxMortgage * MIN_DOWN_PAYMENT_RATIO)
		: 0;

	return {
		state,
		rent,
		income: inputs.income,
		savings,
		expenses: inputs.expenses,
		cashflow:
			inputs.income !== undefined && inputs.expenses !== undefined
				? inputs.income - inputs.expenses
				: undefined,
		totalRent: rent * MORTGAGE_MONTHS,
		priceLow,
		priceHigh,
		propertyLow:
			priceMid > 0
				? Math.round(priceMid * PROPERTY_GROWTH_LOW ** OWN_HOME_HORIZON_YEARS)
				: 0,
		propertyHigh:
			priceMid > 0
				? Math.round(priceMid * PROPERTY_GROWTH_HIGH ** OWN_HOME_HORIZON_YEARS)
				: 0,
		maxPayment: affordability?.maxPayment,
		maxMortgage: affordability?.maxMortgage,
		downPayment,
		savingsGap: affordability ? Math.max(0, downPayment - savings) : undefined,
	};
};

const ownHomeExample = computeOwnHomeContext({
	rent: OWN_HOME_RENT_FALLBACK,
});

export const OWN_HOME_EXAMPLE = {
	rent: OWN_HOME_RENT_FALLBACK,
	property: Math.round(
		(ownHomeExample.priceLow + ownHomeExample.priceHigh) / 2,
	),
};
